import React, { useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import ReactECharts from 'echarts-for-react';
import { deepOrange } from '@mui/material/colors';

const getPercent = (value, divisor) => {
    if (value === 0) {
        return 0;
    }

    const percent = (value / divisor) * 100;

    return Number.isInteger(percent) ? percent : percent.toFixed(2);
};

const EmailOpensChart = ({ total, unique, selected, qrCodesTotal, teamsTotal, allChannelTotal, facebook, linkedin, twitter, share }) => {
    const ref = useRef(null);
    const theme = useTheme();
    const padding = theme.spacing(3);
    const chartHeight = 350;

    const allChannelTotalPercent = getPercent(allChannelTotal, selected);
    const totalPercent = getPercent(total, selected);
    const uniquePercent = getPercent(unique, selected);
    const qrCodesTotalPercent = getPercent(qrCodesTotal, selected);
    const teamsTotalPercent = getPercent(teamsTotal, selected);
    const facebookTotalPercent = getPercent(facebook, selected);
    const linkedinTotalPercent = getPercent(linkedin, selected);
    const twitterTotalPercent = getPercent(twitter, selected);
    const shareTotalPercent = getPercent(share, selected);

    const maxPercent = Math.ceil(Math.max(100, Math.max(allChannelTotalPercent, totalPercent, uniquePercent, qrCodesTotalPercent, teamsTotalPercent, facebookTotalPercent, linkedinTotalPercent, twitterTotalPercent, shareTotalPercent)));

    const options = {
        animationDuration: 500,
        grid: {
            containLabel: true,
            left: padding,
            top: padding,
            right: padding,
            bottom: padding
        },
        xAxis: [
            {
                type: 'value',
                minInterval: 1,
                axisLine: {
                    show: true
                },
                nameLocation: 'middle',
                nameGap: 20,
                name: 'Count'
            },
            {
                type: 'value',
                name: 'As % of Email Audience',
                nameLocation: 'middle',
                nameGap: 20,
                max: maxPercent,
                splitLine: {
                    show: false
                },
                axisLabel: {
                    formatter: (value) => {
                        if (value === 0 || value === 100 || value === maxPercent) {
                            return `${value}%`;
                        }

                        return '';
                    }
                }
            }
        ],
        yAxis: {
            type: 'category',
            data: [
                'Total',
                'Email Total',
                'Email Unique',
                'QR Codes Total',
                'Teams Total',
                'Facebook Total',
                'LinkedIn Total',
                'X (Twitter) Total',
                'Share Total'
            ],
            inverse: true
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow'
            },
            formatter: (args) => {
                let tooltip = `${args[0].axisValue}<br /> `;

                args.forEach(({ marker, seriesName, value }, i) => {
                    tooltip += `${marker} ${seriesName}: <b>${new Intl.NumberFormat('en-GB', { maximumFractionDigits: Number.isInteger(value) ? 0 : 2 }).format(value)}${i === 1 ? '%' : ''}</b></br>`;
                });

                return tooltip;
            }
        },
        series: [
            {
                type: 'bar',
                name: 'Count',
                data: [
                    allChannelTotal,
                    total,
                    unique,
                    qrCodesTotal,
                    teamsTotal,
                    facebook,
                    linkedin,
                    twitter,
                    share
                ],
                xAxisIndex: 0
            },
            {
                type: 'bar',
                name: 'As % of Email Audience',
                data: [
                    allChannelTotalPercent,
                    totalPercent,
                    uniquePercent,
                    qrCodesTotalPercent,
                    teamsTotalPercent,
                    facebookTotalPercent,
                    linkedinTotalPercent,
                    twitterTotalPercent,
                    shareTotalPercent
                ],
                xAxisIndex: 1
            }
        ],
        color: [deepOrange[700], deepOrange[300]]
    };

    return (
        <ReactECharts
            option={options}
            style={{ height: chartHeight }}
            opts={{ renderer: 'svg' }}
            ref={ref}
        />
    );
};

export default EmailOpensChart;